/**
 * Thin abstraction over localStorage so callers don't have to JSON-parse
 * everywhere and so we can swap backends (e.g. for tests).
 */
export interface KeyValueStorage {
  getString(key: string): string | null;
  setString(key: string, value: string): void;
  getJson<T>(key: string): T | null;
  setJson<T>(key: string, value: T): void;
  remove(key: string): void;
  clearNamespace(prefix: string): void;
}

class LocalStorageBackend implements KeyValueStorage {
  getString(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }
  setString(key: string, value: string): void {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* quota / private mode — best-effort */
    }
  }
  getJson<T>(key: string): T | null {
    const raw = this.getString(key);
    if (raw === null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }
  setJson<T>(key: string, value: T): void {
    this.setString(key, JSON.stringify(value));
  }
  remove(key: string): void {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
  clearNamespace(prefix: string): void {
    try {
      const keys: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith(prefix)) keys.push(k);
      }
      keys.forEach((k) => window.localStorage.removeItem(k));
    } catch {
      /* ignore */
    }
  }
}

export const storage: KeyValueStorage = new LocalStorageBackend();
